import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

// App
import { serverUrl } from "../../../config";
import { decodeHTML } from "../hotelData";

// UI components
import { MenuItem, Select } from "@mui/material";
import { Search, X } from "react-feather";

const ANY = "any";
const MAX_RESULTS = 25;
// What one probe asks the endpoint for. Everything past this is invisible to
// the token filter below, so the UI says so rather than quietly truncating.
const PROBE_LIMIT = 200;
const DEBOUNCE_MS = 250;

// `?q=` is a single contiguous LIKE on name OR city, so it cannot answer a
// multi-word query whose words are out of order ("london blu" returns nothing
// while "blu london" is a real hotel). The query is therefore split, the most
// selective word is sent as the probe, and the rest are matched against the
// returned rows. These words are in most of the 826 names and would pull back
// more rows than one probe can carry.
const GENERIC_WORDS = new Set([
  "a",
  "an",
  "and",
  "blu",
  "by",
  "hotel",
  "hotels",
  "of",
  "radisson",
  "the",
]);

// The database collation matches accent-insensitively - ?q=kas returns
// "Radisson Blu Hotel, Kas\u0131" - so the rows coming back have to be folded the
// same way before they are filtered here, or the search hides hotels the
// endpoint just found. NFD covers the combining accents; the stroked letters
// and curly quotes have no decomposition and are mapped by hand.
const FOLD_PAIRS = [
  [/[\u00f8\u00d8]/g, "o"],
  [/[\u00e6\u00c6]/g, "ae"],
  [/[\u0111\u0110]/g, "d"],
  [/[\u0142\u0141]/g, "l"],
  [/\u00df/g, "ss"],
  [/[\u2018\u2019\u02bc`]/g, "'"],
];

const fold = (value) => {
  let out = String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  FOLD_PAIRS.forEach(([re, to]) => {
    out = out.replace(re, to);
  });
  return out;
};

const tokenize = (value) =>
  fold(value)
    .split(/[\s,]+/)
    .filter(Boolean);

// Which words the endpoint is asked for. Length is a poor proxy for rarity on
// its own - "art otel" would probe "otel" (511 hotels) over "art" (23) - so
// the two longest non-generic words are probed together and the narrower
// result set wins. When every word is generic ("radisson blu", "park inn")
// the whole phrase goes over instead, which a contiguous LIKE handles fine.
const MAX_PROBES = 2;

const pickProbes = (query, tokens) => {
  const selective = tokens.filter((t) => !GENERIC_WORDS.has(t));
  if (!selective.length) return [query.trim()];
  return [...selective].sort((a, b) => b.length - a.length).slice(0, MAX_PROBES);
};

const haystack = (hotel) =>
  fold(
    `${hotel.name} ${hotel.city} ${hotel.country} ${hotel.tms_hotel_code}`,
  );

// Prefix on the name beats a mid-word hit, and a shorter name beats a longer
// one holding the same match - "Radisson Blu Hotel, Berlin" above "... Berlin
// Alexanderplatz" for "berlin".
const rankRow = (hotel, tokens) => {
  const name = fold(hotel.name);
  let score = 0;
  tokens.forEach((t) => {
    const at = name.indexOf(t);
    if (at === 0) score += 100;
    else if (at > 0 && /\s|,|-/.test(name[at - 1] || "")) score += 60;
    else if (at > 0) score += 30;
  });
  return score - name.length * 0.02;
};

export default function HotelPicker({
  onSelect,
  selectedCode,
  selectedName,
  isLoading,
}) {
  const [options, setOptions] = useState(null);
  const [region, setRegion] = useState(ANY);
  const [country, setCountry] = useState(ANY);
  const [city, setCity] = useState(ANY);
  const [brand, setBrand] = useState(ANY);
  const [nameQuery, setNameQuery] = useState("");
  const [codeQuery, setCodeQuery] = useState("");
  const [openList, setOpenList] = useState(null);
  const [results, setResults] = useState([]);
  const [searchState, setSearchState] = useState("idle");
  const [truncated, setTruncated] = useState(false);
  const [filterTotal, setFilterTotal] = useState(null);
  const [catalogueError, setCatalogueError] = useState("");

  const blurTimer = useRef(null);
  const searchTimer = useRef(null);
  const abortRef = useRef(null);
  const requestId = useRef(0);

  useEffect(() => {
    let cancelled = false;

    fetch(`${serverUrl}/api/v2/rhg/filter-options?_format=json`, {
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (!cancelled && json) setOptions(json);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      if (blurTimer.current) clearTimeout(blurTimer.current);
      if (searchTimer.current) clearTimeout(searchTimer.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  // Deep links arrive with a code and no name; the loaded hotel supplies it.
  useEffect(() => {
    if (selectedCode) setCodeQuery((current) => current || selectedCode);
    if (selectedName) setNameQuery((current) => current || selectedName);
  }, [selectedCode, selectedName]);

  const filterParams = useCallback(() => {
    const params = new URLSearchParams({ _format: "json", sort: "name" });
    if (region !== ANY) params.append("region", region);
    if (country !== ANY) params.append("country", country);
    if (city !== ANY) params.append("city", city);
    if (brand !== ANY) params.append("brand", brand);
    return params;
  }, [region, country, city, brand]);

  // How many hotels the dropdowns alone match, read from the endpoint rather
  // than counted off a preloaded list.
  useEffect(() => {
    let cancelled = false;
    const params = filterParams();
    params.set("limit", "1");

    fetch(`${serverUrl}/api/v2/rhg/hotels?${params}`, {
      credentials: "include",
    })
      .then((r) =>
        r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)),
      )
      .then((json) => {
        if (cancelled) return;
        setFilterTotal(json?.total ?? 0);
        setCatalogueError("");
      })
      .catch((error) => {
        if (!cancelled) setCatalogueError(error.message);
      });

    return () => {
      cancelled = true;
    };
  }, [filterParams]);

  const runSearch = useCallback(
    (field, raw) => {
      const query = raw.trim();
      if (!query) {
        setResults([]);
        setSearchState("idle");
        setTruncated(false);
        return;
      }

      const tokens = tokenize(query);
      const probes =
        field === "code" ? [query] : pickProbes(query, tokens);

      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const id = ++requestId.current;

      setSearchState("loading");

      const ask = (probe) => {
        const params = filterParams();
        params.set("limit", String(PROBE_LIMIT));
        params.set(field === "code" ? "code" : "q", probe);
        return fetch(`${serverUrl}/api/v2/rhg/hotels?${params}`, {
          credentials: "include",
          signal: controller.signal,
        }).then((r) =>
          r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)),
        );
      };

      Promise.all(probes.map(ask))
        .then((responses) => {
          // A slower earlier keystroke must not overwrite a later one.
          if (id !== requestId.current) return;

          // The narrowest probe is the one least likely to have been cut off
          // at PROBE_LIMIT, so its rows are the ones worth filtering.
          const best = responses.reduce((a, b) =>
            (b?.total ?? Infinity) < (a?.total ?? Infinity) ? b : a,
          );

          const rows = (best?.collection || []).map((h) => ({
            ...h,
            name: decodeHTML(h.name || ""),
          }));

          const matched =
            field === "code"
              ? rows
              : rows
                  .filter((h) => {
                    const hay = haystack(h);
                    return tokens.every((t) => hay.includes(t));
                  })
                  .sort(
                    (a, b) =>
                      rankRow(b, tokens) - rankRow(a, tokens) ||
                      a.name.localeCompare(b.name),
                  );

          setResults(matched.slice(0, MAX_RESULTS));
          setTruncated((best?.total || 0) > PROBE_LIMIT);
          setSearchState("done");
        })
        .catch((error) => {
          if (error.name === "AbortError" || id !== requestId.current) return;
          setResults([]);
          setSearchState("error");
        });
    },
    [filterParams],
  );

  const onQueryChange = (field, value) => {
    if (field === "code") setCodeQuery(value);
    else setNameQuery(value);
    setOpenList(field);

    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(
      () => runSearch(field, value),
      DEBOUNCE_MS,
    );
  };

  const countries = useMemo(() => {
    const list = options?.countries || [];
    return region === ANY ? list : list.filter((c) => c.region === region);
  }, [options, region]);

  const cities = useMemo(() => {
    const list = options?.cities || [];
    return list.filter(
      (c) =>
        (region === ANY || c.region === region) &&
        (country === ANY || c.country === country),
    );
  }, [options, region, country]);

  const choose = (hotel) => {
    setNameQuery(hotel.name);
    setCodeQuery(hotel.tms_hotel_code);
    setOpenList(null);
    setResults([]);
    onSelect(hotel.tms_hotel_code, hotel);
  };

  const clear = () => {
    setNameQuery("");
    setCodeQuery("");
    setResults([]);
    setSearchState("idle");
    setOpenList(null);
  };

  const deferClose = () => {
    blurTimer.current = setTimeout(() => setOpenList(null), 120);
  };

  const submitCode = () => {
    const code = codeQuery.trim().toUpperCase();
    if (!code) return;
    setOpenList(null);
    onSelect(code, null);
  };

  const renderResults = (field) => {
    const query = field === "code" ? codeQuery : nameQuery;
    if (!query.trim()) return null;

    return (
      <div className="rhp-picker__results">
        {searchState === "loading" ? (
          <div className="rhp-picker__result is-empty">Searching</div>
        ) : searchState === "error" ? (
          <div className="rhp-picker__result is-empty">Search failed</div>
        ) : !results.length ? (
          <div className="rhp-picker__result is-empty">No match</div>
        ) : (
          <>
            {results.map((h) => (
              <button
                type="button"
                key={h.tms_hotel_code}
                className={`rhp-picker__result${
                  h.tms_hotel_code === selectedCode ? " is-active" : ""
                }`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(h)}
              >
                <span className="rhp-picker__result-main">
                  {field === "code" ? h.tms_hotel_code : h.name}
                </span>
                <span className="rhp-picker__result-meta">
                  {field === "code" ? h.name : h.tms_hotel_code}
                  {h.city ? ` · ${h.city}` : ""}
                  {h.country ? `, ${h.country}` : ""}
                </span>
              </button>
            ))}
            {truncated ? (
              <div className="rhp-picker__result is-empty">
                {PROBE_LIMIT}+ matches, refine search
              </div>
            ) : null}
          </>
        )}
      </div>
    );
  };

  const dropdown = (label, value, setValue, items, getKey, getLabel) => (
    <div className="rhp-picker__filter">
      <label>{label}</label>
      <Select
        size="small"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          if (label === "Region") {
            setCountry(ANY);
            setCity(ANY);
          }
          if (label === "Country") setCity(ANY);
        }}
      >
        <MenuItem value={ANY}>Any</MenuItem>
        {items.map((item) => (
          <MenuItem key={getKey(item)} value={getKey(item)}>
            {getLabel(item)}
          </MenuItem>
        ))}
      </Select>
    </div>
  );

  return (
    <div className="rhp-picker">
      <div className="rhp-picker__filters">
        {dropdown(
          "Region",
          region,
          setRegion,
          options?.regions || [],
          (r) => r,
          (r) => r,
        )}
        {dropdown(
          "Country",
          country,
          setCountry,
          countries,
          (c) => c.name,
          (c) => c.name,
        )}
        {dropdown("City", city, setCity, cities, (c) => c.name, (c) => c.name)}
        {dropdown(
          "Brand",
          brand,
          setBrand,
          options?.brands || [],
          (b) => b.code,
          (b) => b.name,
        )}
      </div>

      <div className="rhp-picker__inputs">
        <div className="rhp-picker__input">
          <label htmlFor="rhp-hotel-name">Hotel name</label>
          <div className="rhp-picker__field">
            <Search size={15} />
            <input
              id="rhp-hotel-name"
              type="text"
              autoComplete="off"
              placeholder="e.g. Radisson Blu Hotel, London Bloomsbury"
              value={nameQuery}
              onChange={(e) => onQueryChange("name", e.target.value)}
              onFocus={() => {
                setOpenList("name");
                if (nameQuery.trim() && !results.length) {
                  runSearch("name", nameQuery);
                }
              }}
              onBlur={deferClose}
            />
            {nameQuery ? (
              <button type="button" onClick={clear} aria-label="Clear">
                <X size={14} />
              </button>
            ) : null}
          </div>
          {openList === "name" ? renderResults("name") : null}
        </div>

        <span className="rhp-picker__or">OR</span>

        <div className="rhp-picker__input rhp-picker__input--code">
          <label htmlFor="rhp-emma-id">EMMA ID</label>
          <div className="rhp-picker__field">
            <input
              id="rhp-emma-id"
              type="text"
              autoComplete="off"
              placeholder="e.g. GBEDLONBSH"
              value={codeQuery}
              onChange={(e) => onQueryChange("code", e.target.value)}
              onFocus={() => setOpenList("code")}
              onBlur={deferClose}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitCode();
              }}
            />
            {codeQuery ? (
              <button type="button" onClick={clear} aria-label="Clear">
                <X size={14} />
              </button>
            ) : null}
          </div>
          {openList === "code" ? renderResults("code") : null}
        </div>

        <button
          type="button"
          className="rhp-picker__submit"
          disabled={!codeQuery.trim() || isLoading}
          onClick={submitCode}
        >
          {isLoading ? "Loading..." : "Load hotel"}
        </button>
      </div>

      <div className="rhp-picker__status">
        {catalogueError
          ? `Catalogue unavailable: ${catalogueError}`
          : filterTotal == null
            ? ""
            : `${filterTotal.toLocaleString()} hotels`}
      </div>
    </div>
  );
}
